import { Box, Flex, Text } from '@chakra-ui/layout';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
} from '@chakra-ui/react';
import Block from '@uiw/react-color-block';
import { useState } from 'react';
import NumberFormat from 'react-number-format';

import { FormatType, plotColors } from '@revelio/replots';

import { ADAPTIVE_MAP } from '../../hooks/useAdaptiveChartType';
import { usePlotMetricFromItem } from '../../hooks/usePlotMetric';
import { getMetricPaletteConfig } from '../Chart/Map/utils/metric-scales';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../PageConfigProvider/types/chart';
import type {
  DataNormalization,
  PlotSortDirection,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { AdaptiveChartSelector } from './AdaptiveChartSelector';
import { DisplayAsSelector } from './DisplayAsSelector';
import { DivergingBarComboSettings } from './DivergingBarComboSettings';
import { DivergingBarSettings } from './DivergingBarSettings';

const defaultChartTypeShowDataLabels: Partial<Record<ChartType, boolean>> = {
  bar: true,
  'grouped-bar': false,
  sankey: false,
  'diverging-bar': false,
};

const chartWithSingleColor: ChartType[] = [
  'bar',
  'sankey',
  'volume',
  'map',
  'text',
];

const chartWithColorPalette: ChartType[] = [
  'stacked-bar',
  'grouped-bar',
  'line',
  'distribution',
  'box',
  'gauge',
];

const chartWithColor: ChartType[] = [
  ...chartWithSingleColor,
  ...chartWithColorPalette,
];

const chartWithDataLabels: ChartType[] = [
  'bar',
  'grouped-bar',
  'sankey',
  'diverging-bar',
  'diverging-bar-combo',
];
const chartWithZebraStripe: ChartType[] = ['table'];
const chartWithLineOptions: ChartType[] = ['line'];
const chartWithSortOptions: ChartType[] = ['bar'];

const chartWithData: ChartType[] = [
  ...chartWithDataLabels,
  ...chartWithZebraStripe,
];

const chartWithComparisonTransformation: ChartType[] = ['bar', 'diverging-bar'];

type ChartCustomizePanelProps = {
  item: PositionedItem;
  isReport?: boolean;
};

export const ChartCustomizePanel = ({
  item,
  isReport = false,
}: ChartCustomizePanelProps) => {
  const isChartWithAdaptive = (chartType: ChartType | undefined): boolean =>
    chartType !== undefined && ADAPTIVE_MAP[chartType] !== undefined;

  const { dispatch } = useTabContentConfig();
  const id = item?.id;
  const color = item?.plot?.color ?? '#5CA9E9';
  const defaultGaugePalette = ['#55A3FF', '#FF646D'];
  const colorPalette =
    item?.plot?.colorPalette ??
    (item?.plot?.chartType === 'gauge' ? defaultGaugePalette : plotColors);

  const selectedMetric = usePlotMetricFromItem(item);
  const metricConfig = getMetricPaletteConfig(selectedMetric);

  const isDisplayModeEnabled =
    item.plot?.chartType === 'line' &&
    selectedMetric === 'headcount' &&
    !!item.plot?.secondaryDimension &&
    item.plot?.secondaryDimension !== 'timeseries';

  const [accordionIndices, setAccordionIndices] = useState<number[]>([0]);
  const handleAccordionChange = (indices: number[]) => {
    setAccordionIndices(indices);
  };

  const [distributionLowerFocused, setDistributionLowerFocused] =
    useState(false);
  const [distributionUpperFocused, setDistributionUpperFocused] =
    useState(false);
  const [distributionLowerDraft, setDistributionLowerDraft] = useState('');
  const [distributionUpperDraft, setDistributionUpperDraft] = useState('');

  const defaultShowDataLabels = item.plot?.chartType
    ? defaultChartTypeShowDataLabels[item.plot?.chartType]
    : true;

  const { normalization, transformation } = item.plot?.mapOptions ?? {};
  const normalizationMode =
    normalization ?? metricConfig.normalization ?? 'none';
  const transformMode = transformation ?? metricConfig.transformation ?? 'none';

  const isAdvancedMapOptionsEnabled =
    process.env.ENABLE_MAP_CHART_CUSTOMIZATION === 'true';

  return (
    <Box paddingX={2}>
      <Accordion
        allowMultiple
        index={accordionIndices}
        onChange={handleAccordionChange}
      >
        {item.plot?.chartType && item.plot.chartType !== 'table' && (
          <AccordionItem>
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Format
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <Box>
                <Text fontSize="sm" fontWeight="500" mb={1}>
                  Format
                </Text>
                <Select
                  size="sm"
                  aria-label="Format"
                  value={item.plot?.format ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    dispatch({
                      type: 'UPDATE_ITEM_FORMAT',
                      payload: {
                        id,
                        format:
                          value === '' ? undefined : (value as FormatType),
                      },
                    });
                  }}
                >
                  <option value="">Default</option>
                  <option value={FormatType.INTEGER}>Integer</option>
                  <option value={FormatType.CURRENCY}>Currency</option>
                  <option value={FormatType.PERCENTAGE}>Percentage</option>
                  <option value={FormatType.DECIMAL}>Decimal</option>
                  <option value={FormatType.SI}>SI</option>
                </Select>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        )}
        {chartWithColor.includes(item.plot?.chartType as ChartType) && (
          <AccordionItem>
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Color
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              {chartWithColorPalette.includes(
                item.plot?.chartType as ChartType
              ) && (
                <Flex flexWrap="wrap">
                  {colorPalette.map((color, i) => (
                    <Popover key={i} isLazy placement="left">
                      <PopoverTrigger>
                        <Box
                          width="22px"
                          height="22px"
                          borderRadius="4px"
                          marginRight="10px"
                          marginBottom="10px"
                          backgroundColor={color}
                          cursor="pointer"
                        />
                      </PopoverTrigger>
                      <PopoverContent width="170px">
                        <Block
                          color={color}
                          showTriangle={false}
                          colors={[]}
                          onChange={(color) => {
                            const newColorPalette = [
                              ...colorPalette.slice(0, i),
                              color.hex,
                              ...colorPalette.slice(i + 1),
                            ];
                            dispatch({
                              type: 'UPDATE_ITEM_COLOR_PALETTE',
                              payload: { id, colorPalette: newColorPalette },
                            });
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </Flex>
              )}
              {chartWithSingleColor.includes(
                item.plot?.chartType as ChartType
              ) && (
                <Block
                  color={color}
                  showTriangle={false}
                  onChange={(color) => {
                    dispatch({
                      type: 'UPDATE_ITEM_COLOR',
                      payload: { id, color: color.hex },
                    });
                  }}
                  style={{ width: '100%', boxShadow: 'none' }}
                  // TODO: Replace with theme when available
                  colors={[
                    '#65C686',
                    '#82FBAA',
                    '#2D426A',
                    '#5ca9e9',
                    '#E53E3E',
                    '#ECC94B',
                    '#E2E8F0',
                    '#FFFFFF',
                  ]}
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        )}
        {chartWithData.includes(item.plot?.chartType as ChartType) &&
          !(item.plot?.chartType === 'table' && isReport) && (
            <AccordionItem borderTop="none">
              <h2>
                <AccordionButton
                  width="100%"
                  paddingLeft={2}
                  paddingRight=".625rem"
                >
                  <Text
                    as="span"
                    flex="1"
                    textAlign="left"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    Data
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel>
                {chartWithDataLabels.includes(
                  item.plot?.chartType as ChartType
                ) && (
                  <Checkbox
                    colorScheme="green"
                    size="sm"
                    isChecked={
                      item.plot?.showDataLabels === undefined
                        ? defaultShowDataLabels
                        : item.plot?.showDataLabels
                    }
                    onChange={(e) => {
                      dispatch({
                        type: 'UPDATE_ITEM_SHOW_DATA_LABELS',
                        payload: { id, showDataLabels: e.target.checked },
                      });
                    }}
                  >
                    Show Value Labels
                  </Checkbox>
                )}
                {chartWithSortOptions.includes(
                  item.plot?.chartType as ChartType
                ) && (
                  <Box mt={2}>
                    <Text fontSize="sm" fontWeight="500" mb={1}>
                      Sort
                    </Text>
                    <Select
                      size="sm"
                      aria-label="Sort"
                      value={item.plot?.sort ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        dispatch({
                          type: 'UPDATE_ITEM_SORT',
                          payload: {
                            id,
                            sort:
                              value === ''
                                ? null
                                : (value as PlotSortDirection),
                          },
                        });
                      }}
                    >
                      <option value="">N/A</option>
                      <option value="ASC">Ascending</option>
                      <option value="DESC">Descending</option>
                    </Select>
                  </Box>
                )}
                {item.plot?.chartType === 'bar' && (
                  <Flex mt={2} gap={2}>
                    <Box>
                      <Text fontSize="sm" fontWeight="500" mb={1}>
                        Min value
                      </Text>
                      <Input
                        size="sm"
                        width="6rem"
                        type="number"
                        step="0.1"
                        aria-label="Bar adjusted min"
                        placeholder="Auto"
                        value={item.plot?.minValue ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const minValue =
                            value === '' ? undefined : Number(value);
                          if (
                            minValue === undefined ||
                            !Number.isNaN(minValue)
                          ) {
                            dispatch({
                              type: 'UPDATE_ITEM_BAR_AXIS_RANGE',
                              payload: {
                                id,
                                maxValue: item.plot?.maxValue,
                                minValue,
                              },
                            });
                          }
                        }}
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="500" mb={1}>
                        Max value
                      </Text>
                      <Input
                        size="sm"
                        width="6rem"
                        type="number"
                        step="0.1"
                        aria-label="Bar max value"
                        placeholder="Auto"
                        value={item.plot?.maxValue ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const maxValue =
                            value === '' ? undefined : Number(value);
                          if (
                            maxValue === undefined ||
                            !Number.isNaN(maxValue)
                          ) {
                            dispatch({
                              type: 'UPDATE_ITEM_BAR_AXIS_RANGE',
                              payload: {
                                id,
                                maxValue,
                                minValue: item.plot?.minValue,
                              },
                            });
                          }
                        }}
                      />
                    </Box>
                  </Flex>
                )}
                {chartWithZebraStripe.includes(
                  item.plot?.chartType as ChartType
                ) && (
                  <Flex direction="column" gap={2}>
                    <Checkbox
                      colorScheme="green"
                      size="sm"
                      isChecked={
                        item.plot?.showZebraStripe === undefined
                          ? true
                          : item.plot?.showZebraStripe
                      }
                      onChange={(e) => {
                        dispatch({
                          type: 'UPDATE_ITEM_SHOW_ZEBRA_STRIPE',
                          payload: { id, showZebraStripe: e.target.checked },
                        });
                      }}
                    >
                      Show Zebra Stripe
                    </Checkbox>
                    <Checkbox
                      colorScheme="green"
                      size="sm"
                      isChecked={
                        item.plot?.enableRowSelection === undefined
                          ? true
                          : item.plot?.enableRowSelection
                      }
                      onChange={(e) => {
                        dispatch({
                          type: 'UPDATE_ITEM_ENABLE_ROW_SELECTION',
                          payload: {
                            id,
                            enableRowSelection: e.target.checked,
                          },
                        });
                      }}
                    >
                      Enable Row Selection
                    </Checkbox>
                  </Flex>
                )}
              </AccordionPanel>
            </AccordionItem>
          )}
        {chartWithLineOptions.includes(item.plot?.chartType as ChartType) && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Display Options
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <Flex direction="column" gap={2}>
                {isDisplayModeEnabled && <DisplayAsSelector item={item} />}
                <Checkbox
                  colorScheme="green"
                  size="sm"
                  isChecked={item.plot?.showDiffLabel ?? false}
                  onChange={(e) => {
                    dispatch({
                      type: 'UPDATE_ITEM_SHOW_DIFF_LABEL',
                      payload: { id, showDiffLabel: e.target.checked },
                    });
                  }}
                >
                  Show Diff Label
                </Checkbox>
                <Checkbox
                  colorScheme="green"
                  size="sm"
                  isChecked={item.plot?.showLegend ?? false}
                  onChange={(e) => {
                    dispatch({
                      type: 'UPDATE_ITEM_SHOW_LEGEND',
                      payload: { id, showLegend: e.target.checked },
                    });
                  }}
                >
                  Show Legend
                </Checkbox>
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb={1}>
                    Curve Smoothness
                  </Text>
                  <Flex gap={3} align="center">
                    <Slider
                      flex={1}
                      aria-label="Curve smoothness"
                      min={0}
                      max={1}
                      step={0.1}
                      value={item.plot?.curveSmoothness ?? 0}
                      onChange={(val) => {
                        dispatch({
                          type: 'UPDATE_ITEM_CURVE_SMOOTHNESS',
                          payload: { id, curveSmoothness: val },
                        });
                      }}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <Text fontSize="sm" minWidth="2rem">
                      {Number(item.plot?.curveSmoothness ?? 0).toFixed(1)}
                    </Text>
                  </Flex>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb={1}>
                    Font size
                  </Text>
                  <Input
                    size="sm"
                    width="4rem"
                    type="number"
                    aria-label="Font size"
                    value={item.plot?.fontSize ?? 11}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (!Number.isNaN(val)) {
                        dispatch({
                          type: 'UPDATE_ITEM_FONT_SIZE',
                          payload: { id, fontSize: val },
                        });
                      }
                    }}
                  />
                </Box>
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        )}
        {item.plot?.chartType === 'grouped-bar' && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Display Options
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <Box>
                <Text fontSize="sm" fontWeight="500" mb={1}>
                  Orientation
                </Text>
                <RadioGroup
                  value={item.plot?.orientation ?? 'horizontal'}
                  onChange={(val) => {
                    if (val !== 'horizontal' && val !== 'vertical') return;
                    dispatch({
                      type: 'UPDATE_ITEM_ORIENTATION',
                      payload: { id, orientation: val },
                    });
                  }}
                >
                  <Stack direction="column">
                    <Radio value="horizontal" size="sm" colorScheme="green">
                      Horizontal
                    </Radio>
                    <Radio value="vertical" size="sm" colorScheme="green">
                      Vertical
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        )}
        {item.plot?.chartType === 'treemap' && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Size normalization
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <RadioGroup
                value={item.plot?.treemap?.sizeTransform ?? 'none'}
                onChange={(val) => {
                  if (val !== 'none' && val !== 'sqrt') return;
                  dispatch({
                    type: 'UPDATE_ITEM_TREEMAP_SIZE_TRANSFORM',
                    payload: { id, treemapSizeTransform: val },
                  });
                }}
              >
                <Stack direction="column">
                  <Radio value="none">None</Radio>
                  <Radio value="sqrt">Square Root (sqrt)</Radio>
                </Stack>
              </RadioGroup>
            </AccordionPanel>
          </AccordionItem>
        )}
        {item.plot?.chartType === 'distribution' && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Distribution Range
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <FormControl
                isInvalid={(() => {
                  const lower = item.plot?.distributionCutoffLower ?? 1;
                  const upper = item.plot?.distributionCutoffUpper ?? 99;
                  return (
                    lower >= upper ||
                    lower < 0 ||
                    lower > 100 ||
                    upper < 0 ||
                    upper > 100
                  );
                })()}
              >
                <Flex direction="column" gap={3}>
                  <Box>
                    <Text fontSize="sm" fontWeight="500" mb={1}>
                      Lower cutoff
                    </Text>
                    <NumberFormat
                      customInput={Input}
                      size="sm"
                      width="6rem"
                      suffix="%"
                      decimalScale={0}
                      thousandSeparator={false}
                      allowNegative={false}
                      aria-label="Lower cutoff percent"
                      value={
                        distributionLowerFocused
                          ? distributionLowerDraft
                          : String(item.plot?.distributionCutoffLower ?? 1)
                      }
                      onFocus={() => {
                        setDistributionLowerFocused(true);
                        setDistributionLowerDraft(
                          String(item.plot?.distributionCutoffLower ?? 1)
                        );
                      }}
                      onBlur={() => {
                        setDistributionLowerFocused(false);
                        const trimmed = distributionLowerDraft.trim();
                        if (trimmed === '') {
                          dispatch({
                            type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                            payload: {
                              id,
                              distributionCutoffLower: undefined,
                              distributionCutoffUpper:
                                item.plot?.distributionCutoffUpper,
                            },
                          });
                          return;
                        }
                        const num = Number(trimmed);
                        if (!Number.isNaN(num)) {
                          const clamped = Math.min(100, Math.max(0, num));
                          dispatch({
                            type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                            payload: {
                              id,
                              distributionCutoffLower: clamped,
                              distributionCutoffUpper:
                                item.plot?.distributionCutoffUpper,
                            },
                          });
                        }
                      }}
                      onValueChange={(values) => {
                        setDistributionLowerDraft(values.value ?? '');
                      }}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="500" mb={1}>
                      Upper cutoff
                    </Text>
                    <NumberFormat
                      customInput={Input}
                      size="sm"
                      width="6rem"
                      suffix="%"
                      decimalScale={0}
                      thousandSeparator={false}
                      allowNegative={false}
                      aria-label="Upper cutoff percent"
                      value={
                        distributionUpperFocused
                          ? distributionUpperDraft
                          : String(item.plot?.distributionCutoffUpper ?? 99)
                      }
                      onFocus={() => {
                        setDistributionUpperFocused(true);
                        setDistributionUpperDraft(
                          String(item.plot?.distributionCutoffUpper ?? 99)
                        );
                      }}
                      onBlur={() => {
                        setDistributionUpperFocused(false);
                        const trimmed = distributionUpperDraft.trim();
                        if (trimmed === '') {
                          dispatch({
                            type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                            payload: {
                              id,
                              distributionCutoffLower:
                                item.plot?.distributionCutoffLower,
                              distributionCutoffUpper: undefined,
                            },
                          });
                          return;
                        }
                        const num = Number(trimmed);
                        if (!Number.isNaN(num)) {
                          const clamped = Math.min(100, Math.max(0, num));
                          dispatch({
                            type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                            payload: {
                              id,
                              distributionCutoffLower:
                                item.plot?.distributionCutoffLower,
                              distributionCutoffUpper: clamped,
                            },
                          });
                        }
                      }}
                      onValueChange={(values) => {
                        setDistributionUpperDraft(values.value ?? '');
                      }}
                    />
                  </Box>
                </Flex>
                <FormErrorMessage mt={2}>
                  Values must be between 0 and 100; lower must be less than
                  upper.
                </FormErrorMessage>
              </FormControl>
            </AccordionPanel>
          </AccordionItem>
        )}
        {isChartWithAdaptive(item.plot?.chartType) && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Adaptive
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <AdaptiveChartSelector item={item} />
            </AccordionPanel>
          </AccordionItem>
        )}
        {chartWithComparisonTransformation.includes(
          item.plot?.chartType as ChartType
        ) && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Comparison
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <Stack spacing={2}>
                <Checkbox
                  colorScheme="green"
                  size="sm"
                  isChecked={!!item.plot?.comparisonTransformation}
                  onChange={(e) => {
                    if (e.target.checked) {
                      dispatch({
                        type: 'UPDATE_ITEM_COMPARISON_TRANSFORMATION',
                        payload: {
                          id,
                          comparisonTransformation: {
                            baseline: 'second',
                          },
                        },
                      });
                    } else {
                      dispatch({
                        type: 'UPDATE_ITEM_COMPARISON_TRANSFORMATION',
                        payload: {
                          id,
                          comparisonTransformation: undefined,
                        },
                      });
                    }
                  }}
                >
                  Show Difference
                </Checkbox>
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}
        {item.plot?.chartType === 'diverging-bar' && (
          <DivergingBarSettings item={item} dispatch={dispatch} />
        )}
        {item.plot?.chartType === 'diverging-bar-combo' && (
          <DivergingBarComboSettings item={item} dispatch={dispatch} />
        )}
        {(item.plot?.chartType === 'box' ||
          item.plot?.chartType === 'distribution') && (
          <AccordionItem borderTop="none">
            <h2>
              <AccordionButton
                width="100%"
                paddingLeft={2}
                paddingRight=".625rem"
              >
                <Text
                  as="span"
                  flex="1"
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Display Options
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <Checkbox
                colorScheme="green"
                size="sm"
                isChecked={item.plot?.showQuartiles ?? false}
                onChange={(e) => {
                  dispatch({
                    type: 'UPDATE_ITEM_SHOW_QUARTILES',
                    payload: { id, showQuartiles: e.target.checked },
                  });
                }}
              >
                Show interquartile range
              </Checkbox>
            </AccordionPanel>
          </AccordionItem>
        )}
        {isAdvancedMapOptionsEnabled && item.plot?.chartType === 'map' && (
          <>
            <AccordionItem borderTop="none">
              <h2>
                <AccordionButton
                  width="100%"
                  paddingLeft={2}
                  paddingRight=".625rem"
                >
                  <Text
                    as="span"
                    flex="1"
                    textAlign="left"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    Normalization
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel>
                <RadioGroup
                  value={normalizationMode}
                  onChange={(val) => {
                    dispatch({
                      type: 'UPDATE_ITEM_NORMALIZATION',
                      payload: {
                        id,
                        normalization: val as DataNormalization,
                      },
                    });
                  }}
                >
                  <Flex direction="column" gap={1}>
                    <Radio value="none" size="sm" colorScheme="green">
                      None (raw count)
                    </Radio>
                    <Radio value="area_density" size="sm" colorScheme="green">
                      By geographic area
                    </Radio>
                  </Flex>
                </RadioGroup>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem borderTop="none">
              <h2>
                <AccordionButton
                  width="100%"
                  paddingLeft={2}
                  paddingRight=".625rem"
                >
                  <Text
                    as="span"
                    flex="1"
                    textAlign="left"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    Transformation
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel>
                <RadioGroup
                  value={transformMode}
                  onChange={(val) => {
                    dispatch({
                      type: 'UPDATE_ITEM_TRANSFORMATION',
                      payload: {
                        id,
                        transformation: val as 'none' | 'sqrt' | 'log',
                      },
                    });
                  }}
                >
                  <Flex direction="column" gap={1}>
                    <Radio value="none" size="sm" colorScheme="green">
                      None
                    </Radio>
                    <Radio value="sqrt" size="sm" colorScheme="green">
                      Square Root
                    </Radio>
                    <Radio value="log" size="sm" colorScheme="green">
                      Logarithmic
                    </Radio>
                  </Flex>
                </RadioGroup>
              </AccordionPanel>
            </AccordionItem>
          </>
        )}
      </Accordion>
    </Box>
  );
};
